import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EyeOutlined } from '@ant-design/icons';
import { Spin, Empty, List, Button, Drawer } from 'antd';
import { formatDate, DATE_FORMATS } from '@/utils/formUtils';
import { getUserFullName } from '@/utils/utils';
import SnapshotFields from './SnapshotFields';

/**
 * SnapshotsTab
 *
 * Lists every full-state capture of a record and opens one as a field table.
 */
export default function SnapshotsTab({ data, loading, modelName }) {
  const { t, i18n } = useTranslation('history');
  const locale = i18n.language || 'sr';
  const [selectedSnapshot, setSelectedSnapshot] = useState(null);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return <Empty description={t('ui.noSnapshots')} className="my-8" />;
  }

  return (
    <div className="p-2">
      <List
        dataSource={data}
        renderItem={(snapshot) => {
          const userName = snapshot.user
            ? getUserFullName(snapshot.user.firstName, snapshot.user.lastName)
            : t('ui.system');

          return (
            <List.Item
              actions={[
                <Button
                  key="view"
                  type="link"
                  icon={<EyeOutlined />}
                  onClick={() => setSelectedSnapshot(snapshot)}
                >
                  {t('ui.viewSnapshot')}
                </Button>,
              ]}
            >
              <List.Item.Meta
                title={`${formatDate(
                  snapshot.timestamp,
                  DATE_FORMATS.DISPLAY_DOTS
                )} ${formatDate(snapshot.timestamp, DATE_FORMATS.TIME_ONLY)}`}
                description={`${t('field.createdBy')}: ${userName}`}
              />
            </List.Item>
          );
        }}
      />

      <Drawer
        title={t('ui.snapshot')}
        placement="right"
        onClose={() => setSelectedSnapshot(null)}
        open={Boolean(selectedSnapshot)}
        width={720}
      >
        {selectedSnapshot && (
          <div>
            <div className="mb-4 rounded border border-gray-200 p-3 text-sm">
              <div>
                <span className="font-medium">{t('field.updatedAt')}:</span>{' '}
                {formatDate(selectedSnapshot.timestamp, DATE_FORMATS.DISPLAY_DOTS)}{' '}
                {formatDate(selectedSnapshot.timestamp, DATE_FORMATS.TIME_ONLY)}
              </div>
              <div>
                <span className="font-medium">{t('field.createdBy')}:</span>{' '}
                {selectedSnapshot.user
                  ? getUserFullName(
                      selectedSnapshot.user.firstName,
                      selectedSnapshot.user.lastName
                    )
                  : t('ui.system')}
              </div>
            </div>

            <SnapshotFields
              data={selectedSnapshot.data}
              modelName={modelName}
              t={t}
              locale={locale}
            />
          </div>
        )}
      </Drawer>
    </div>
  );
}
